import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Lock, Sparkle, Lightning, EnvelopeSimple, ArrowSquareOut, AddressBook, ArrowRight, Handshake, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import InterestModal from "@/components/InterestModal";
import { useSiteSettings } from "@/context/SiteSettingsContext";

// Hand-built vCard 3.0 — works on iOS/Android Save-to-Contacts
function buildVCard({ org, title, email, url, address }) {
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `ORG:${org}`,
    title ? `TITLE:${title}` : null,
    email ? `EMAIL;TYPE=INTERNET,WORK:${email}` : null,
    url ? `URL:${url}` : null,
    address ? `ADR;TYPE=WORK:;;${address.replace(/\n/g, " ")};;;;` : null,
    "END:VCARD",
  ].filter(Boolean);
  return lines.join("\r\n");
}

/**
 * /card — Digital business card. Mobile-first, share via URL/NFC/QR.
 * Honours `?promo=...` to unlock the gated event banner site-wide.
 */
export default function DigitalCard() {
  const { settings } = useSiteSettings();
  const [searchParams] = useSearchParams();
  const [contact, setContact] = useState({ email: "hello@appistry.tech", address: "Kent, United Kingdom" });
  const [interestOpen, setInterestOpen] = useState(false);

  useEffect(() => {
    // Persist promo code from URL — same lock as the magic /event redirect
    const code = searchParams.get("promo");
    if (code) {
      try { localStorage.setItem("appistry.promo", code); } catch (_) {}
    }
  }, [searchParams]);

  useEffect(() => {
    api.get("/site-content").then(({ data }) => {
      const f = data?.footer || {};
      setContact({
        email: f.contact_email || "hello@appistry.tech",
        address: f.address || "Kent, United Kingdom",
      });
    }).catch(() => {});
  }, []);

  const saveContact = () => {
    const vcard = buildVCard({
      org: "Appistry",
      title: "Practical apps, thoughtfully built",
      email: contact.email,
      url: "https://appistry.tech",
      address: contact.address,
    });
    const blob = new Blob([vcard], { type: "text/vcard" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "Appistry.vcf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="digital-card">
      <div className="max-w-md mx-auto bg-white shadow-2xl min-h-screen">
        {/* Hero */}
        <div className="relative px-7 pt-12 pb-10 text-white overflow-hidden" style={{ background: "linear-gradient(135deg, #14143F 0%, #1F1A4A 50%, #0570DE 200%)" }}>
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle, #FFFFFF 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 flex items-center justify-center bg-white">
                <span className="text-xl font-black tracking-tighter" style={{ color: "#0570DE", fontFamily: "'Outfit', sans-serif" }}>A</span>
              </div>
              <div className="text-2xl font-black tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>Appistry</div>
            </div>
            <p className="text-[10px] font-mono uppercase tracking-[3px] text-zinc-400 mb-3">PRACTICAL APPS, THOUGHTFULLY BUILT</p>
            <h1 className="text-3xl font-black tracking-tighter leading-[1.05] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Niche software,<br/><span style={{ color: "#5EAEFF" }}>built like it matters.</span>
            </h1>
            <p className="text-sm leading-relaxed text-zinc-300">
              We co-build the unglamorous apps off-the-shelf SaaS has ignored — for therapists, home bakers, residents&rsquo; associations, and the small businesses in between.
            </p>
          </div>
        </div>

        {/* Quick actions */}
        <div className="px-7 py-5 grid grid-cols-3 gap-2 border-b border-zinc-200 bg-white">
          <a href={`mailto:${contact.email}`} className="flex flex-col items-center gap-1.5 py-3 border border-zinc-200 hover:border-zinc-950 transition-colors" data-testid="card-email">
            <EnvelopeSimple size={20} weight="bold" className="text-zinc-950" />
            <span className="text-[10px] font-mono uppercase tracking-widest font-bold">Email</span>
          </a>
          <a href="https://appistry.tech" className="flex flex-col items-center gap-1.5 py-3 border border-zinc-200 hover:border-zinc-950 transition-colors" data-testid="card-website">
            <ArrowSquareOut size={20} weight="bold" className="text-zinc-950" />
            <span className="text-[10px] font-mono uppercase tracking-widest font-bold">Website</span>
          </a>
          <button onClick={saveContact} className="flex flex-col items-center gap-1.5 py-3 border-2 border-zinc-950 bg-zinc-950 text-white" data-testid="card-save-contact">
            <AddressBook size={20} weight="bold" />
            <span className="text-[10px] font-mono uppercase tracking-widest font-bold">Save</span>
          </button>
        </div>

        {/* Consultation hook */}
        <div className="m-7 p-5 text-white" style={{ backgroundColor: "#0570DE" }} data-testid="card-consultation">
          <div className="flex items-start gap-3">
            <Lightning size={20} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[9px] font-mono uppercase tracking-[2.5px] opacity-80 font-bold">FOR YOU · NETWORKING-EVENT GUEST</p>
              <p className="text-base font-bold leading-tight mt-1">Free 30-minute Practical Build consultation.</p>
              <p className="text-[12px] mt-1.5 opacity-95 leading-snug mb-3">Walk away with one shippable idea and a plan. We promise no slide deck.</p>
              <a href="/event/hl1may" className="inline-flex items-center gap-1.5 bg-white text-[#0570DE] px-3 py-1.5 text-xs font-bold" data-testid="card-book-consultation">
                Claim my 30 minutes <ArrowRight size={12} weight="bold" />
              </a>
            </div>
          </div>
        </div>

        {/* The 3 apps */}
        <div className="px-7 pb-2">
          <p className="text-[10px] font-mono uppercase tracking-[2.5px] text-zinc-500 mb-3 font-bold">WHAT WE'RE BUILDING</p>
          <div className="space-y-2.5">
            <CardAppRow
              href="https://reflect-ai-7.emergent.host"
              external
              icon={Sparkle}
              live
              category="LIVE NOW"
              title="ReflectiveMind"
              line="AI journaling for the quietly curious."
            />
            <CardAppRow
              href="/demos/chapter-one"
              icon={BookOpen}
              category="CONCEPT"
              title="Book Club"
              line="A warmer home for local reading clubs."
            />
            <CardAppRow
              href="/demos/quietroom"
              icon={Lock}
              category="CONCEPT"
              title="QuietRoom"
              line="Encrypted practice diary for therapists."
            />
          </div>
        </div>

        {/* Co-innovation */}
        {settings.co_innovation_enabled && (
          <div className="m-7 mt-5 p-5 border-2 border-dashed border-zinc-300" data-testid="card-coinnovation">
            <div className="flex items-start gap-3">
              <Handshake size={20} weight="fill" className="text-zinc-950 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[9px] font-mono uppercase tracking-[2.5px] text-zinc-500 font-bold">CO-INNOVATION · H1 2026</p>
                <p className="text-base font-bold leading-tight mt-1">3 founding-partner places.</p>
                <p className="text-[12px] mt-1.5 leading-snug text-zinc-700">Bring a problem. We co-build it. You use it free for 6 months, then forever at half-price.</p>
                <a href="/co-innovation" className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold underline underline-offset-4" data-testid="card-coinnovation-link">
                  See the programme →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Register interest fallback */}
        <div className="px-7 pb-7 text-center">
          <button onClick={() => setInterestOpen(true)} className="text-sm font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-950 transition-colors" data-testid="card-interest">
            Or just register interest →
          </button>
        </div>

        {/* Footer */}
        <div className="px-7 py-5 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-zinc-500">{contact.email}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">{contact.address}</p>
          </div>
          <div className="flex items-center gap-1 text-zinc-400">
            <ShieldCheck size={12} weight="fill" />
            <span className="text-[9px] font-mono uppercase tracking-widest">UK · GDPR</span>
          </div>
        </div>
      </div>

      <InterestModal
        open={interestOpen}
        onClose={() => setInterestOpen(false)}
        source="digital_card"
        title="Get on the list"
        body="Drop your email — we'll let you know as new concepts go live."
        ctaLabel="Register interest"
      />
    </div>
  );
}

function CardAppRow({ href, external, icon: Icon, live, category, title, line }) {
  const target = external ? "_blank" : undefined;
  const rel = external ? "noreferrer" : undefined;
  return (
    <a href={href} target={target} rel={rel} className="block p-3 border transition-colors hover:border-zinc-950" style={{ borderColor: live ? "#0570DE" : "#E4E4E7", backgroundColor: live ? "#EFF6FF" : "#FFFFFF" }} data-testid={`card-app-${title.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: live ? "#0570DE" : "#0A0A0A" }}>
          <Icon size={16} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-base font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</span>
            <span className="text-[8px] font-mono uppercase tracking-widest font-bold" style={{ color: live ? "#0570DE" : "#71717A" }}>{live ? "● " : ""}{category}</span>
          </div>
          <p className="text-xs text-zinc-600 leading-snug">{line}</p>
        </div>
        <ArrowRight size={14} weight="bold" className="text-zinc-400 flex-shrink-0" />
      </div>
    </a>
  );
}

